import { Controller, useFormContext } from "react-hook-form"
import {
  OnChangePostalCodeParams,
  PostalCodeController,
} from "../../../../../PostalCodeController"
import { Trans, t } from "@lingui/macro"
import { useEffect, useMemo } from "react"

import { DropdownField } from "../../../../../../../../../../components/DropdownField"
import { DualColumnRow } from "../../../../../../style"
import { ReceiverForm } from "../ReceiverForm"
import { ShippingMethodPData } from "../../../../types"
import debounce from "lodash/debounce"
import { useGeolocation } from "../../../../../../../../providers/GeoLocationProvider/GeolocationContext"

export const ShippingMethodP = () => {
  const {
    control,
    errors,
    setValue,
    watch,
    getValues,
  } = useFormContext<ShippingMethodPData>()
  const {
    data,
    loading,
    fetchGeolocation,
    resetGeolocation,
    errorLocation,
  } = useGeolocation()

  const postalCode = watch("destinationPostalCode")

  const debouncedFetchGeolocation = useMemo(
    () =>
      debounce(code => {
        fetchGeolocation(code)
      }, 500),
    [fetchGeolocation]
  )

  const stateOptions = useMemo(
    () =>
      postalCode && data?.objects.states
        ? data.objects.states.map(state => ({
            id: state.stateId,
            name: state.name,
            value: state.name,
          }))
        : [],
    [postalCode, data?.objects.states]
  )

  const cityOptions = useMemo(
    () =>
      postalCode && data?.objects.cities
        ? data.objects.cities.map((city, i) => ({
            id: i.toString(),
            name: city.name,
            value: city.name,
          }))
        : [],
    [postalCode, data?.objects.cities]
  )

  useEffect(() => {
    setValue("destinationState", stateOptions[0]?.value ?? "")
  }, [setValue, stateOptions])

  useEffect(() => {
    setValue("destinationCity", cityOptions[0]?.value ?? "")
  }, [setValue, cityOptions])

  useEffect(() => {
    if (!postalCode) {
      setValue("destinationState", "")
      setValue("destinationCity", "")
    }
  }, [setValue, postalCode])

  useEffect(() => {
    if (errorLocation) {
      setValue("destinationState", "")
      setValue("destinationCity", "")
    }
  }, [setValue, errorLocation])

  useEffect(() => {
    return () => {
      resetGeolocation()
    }
  }, [resetGeolocation])

  const handlePostalCodeChange = ({
    value,
    postalCodeLenght,
  }: OnChangePostalCodeParams) => {
    const minLength = postalCodeLenght

    if (value.length >= minLength) {
      debouncedFetchGeolocation(value)
    } else if (data !== null) {
      resetGeolocation()
    }
  }

  return (
    <>
      <ReceiverForm
        title={t`¿Quién retira?`}
        fields={{
          receiverPhone: false,
        }}
      />

      <DualColumnRow>
        <PostalCodeController
          name="destinationPostalCode"
          control={control}
          errorMsg={errors.destinationPostalCode?.message}
          disabled={loading}
          onChangePostalCode={handlePostalCodeChange}
          hasLocationError={!!errorLocation}
          defaultValue={getValues("destinationPostalCode")}
        />
      </DualColumnRow>

      <Controller
        control={control}
        name="destinationCity"
        defaultValue=""
        rules={{ required: true }}
        render={({ onChange, value, name }) => (
          <DropdownField
            name={name}
            label={<Trans>Localidad</Trans>}
            dataTestId={"shipping-method-city-dropdown"}
            errorMsg={errors.destinationCity?.message}
            options={cityOptions}
            disabled={cityOptions.length < 2}
            loading={loading}
            onChange={onChange}
            value={value}
          />
        )}
      />

      <Controller
        control={control}
        name="destinationState"
        defaultValue=""
        rules={{ required: true }}
        render={({ onChange, value }) => (
          <DropdownField
            name="destinationState"
            label={<Trans>Provincia</Trans>}
            options={stateOptions}
            errorMsg={errors.destinationState?.message}
            disabled={stateOptions.length < 2}
            dataTestId={"shipping-method-state-dropdown"}
            value={value}
            loading={loading}
            onChange={selectedStateName => {
              onChange(selectedStateName)

              const selectedState = stateOptions.find(
                state => state.name === selectedStateName
              )

              if (postalCode && selectedState && stateOptions.length > 1) {
                fetchGeolocation(postalCode, selectedState.id)
              }
            }}
          />
        )}
      />
    </>
  )
}
